import clsx from "clsx";
import { eachDayOfInterval, format } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { Button, LoadPanel, ScrollView } from "devextreme-react";
import Calendar, { CalendarRef } from "devextreme-react/calendar";
import notify from "../../lib/notify";
import i18n from "i18next";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadAvailability } from "../../api/calendarData";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import axios from "../../lib/axios";
import { WorkPlan } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./WorkPlanForEmployee.scss";

const currentDate = new Date();

const formatTime = (time: string) => {
  const dateObj = new Date(time);
  return dateObj.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatTimeWithoutSeconds = (timeString: string) => {
  const [hours, minutes] = timeString.split(":");
  return `${hours}:${minutes}`;
};

type WorkPlanForEmployeeProps = {
  showHeader?: boolean;
};

export default function WorkPlanForEmployee({
  showHeader = true,
}: WorkPlanForEmployeeProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { companyId } = useCompanyContext();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const calendarRef = useRef<CalendarRef>(null);

  const [startDate, setStartDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
  );
  const [endDate, setEndDate] = useState(
    new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0),
  );
  const [days, setDays] = useState<WorkPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [availabilityData, setAvailabilityData] = useState<any[]>([]);

  // Index maps for O(1) lookup
  const daysMap = useMemo(() => {
    const map = new Map<string, WorkPlan>();
    for (const d of days) {
      map.set(d.date, d);
    }
    return map;
  }, [days]);

  const availabilityMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const entry of availabilityData) {
      map.set(entry.date, entry);
    }
    return map;
  }, [availabilityData]);

  useEffect(() => {
    const fetchData = () => {
      setLoading(true);

      axios
        .get<WorkPlan[]>("/api/work-plan", {
          params: {
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
          },
          headers: {
            "Accept-Language": i18n.language,
          },
        })
        .then((response) => {
          setDays(response.data);
        })
        .catch(() => {
          notify("Wystapil blad podczas pobierania danych.", "error");
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchData();
  }, [startDate, endDate]);

  useEffect(() => {
    if (!user || user.employeeId === undefined || user.companyId === undefined)
      return;

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const employeeId = user.employeeId!;
    const company = companyId === 0 ? user.companyId! : companyId;

    fetchDataForAvailability(year, month, [company], [employeeId]);
  }, [currentMonth]);

  const fetchDataForAvailability = async (
    year: number,
    month: number,
    companyIds?: number[],
    employeeIds?: number[],
  ) => {
    const idsToFetch =
      employeeIds && employeeIds.length > 0
        ? employeeIds
        : user?.employeeId
          ? [user.employeeId]
          : [];

    if (idsToFetch.length === 0) return;

    const companyIdsToFetch =
      companyIds && companyIds.length > 0
        ? companyIds
        : user?.companyId
          ? [user.companyId]
          : [];

    if (companyIdsToFetch.length === 0) return;

    try {
      const response = await downloadAvailability(
        year,
        month,
        companyIdsToFetch,
        employeeIds,
      );

      if (!response.isOk || !response.data) {
        notify(
          `${t("Error:")} ${response.error || t("Unknown error")}`,
          "error",
        );
        return;
      }

      const formattedData: any[] = [];

      response.data.forEach((employeeAvailability: any) => {
        const entries = employeeAvailability.entries || [];

        entries.forEach((day: any) => {
          let positions = day.positions || [];

          if (positions.length === 0) {
            positions = [
              {
                from: "2025-02-27T08:00:00",
                to: "2025-02-27T16:00:00",
              },
            ];
          }

          formattedData.push({
            date: day.date.split("T")[0],
            available: !!day.sum,
            startTime: day.min || "",
            endTime: day.max || "",
            sum: day.sum || "",
            positions,
            notes: day.note || "",
            dateType: day.dateType || "",
          });
        });
      });

      setAvailabilityData(formattedData);
    } catch (error) {
      notify(`${t("Error:")} ${error || t("Unknown error")}`, "error");
    }
  };

  const localizeFirstLine = useCallback(
    (day: WorkPlan | undefined): string => {
      if (!day) return "";
      if (day.dayType === 2) return t("Day off");
      if (day.dayType === 3) return t("Holiday");
      return day.firstLine;
    },
    [t],
  );

  const getAvailabilityText = useCallback(
    (date: Date) => {
      const formattedDate = date.toLocaleDateString("en-CA");
      const availabilityInfo = availabilityMap.get(formattedDate);

      if (
        availabilityInfo &&
        availabilityInfo.available &&
        availabilityInfo.startTime &&
        availabilityInfo.endTime &&
        availabilityInfo.sum
      ) {
        return `* ${formatTime(availabilityInfo.startTime)}-${formatTime(
          availabilityInfo.endTime,
        )} (${formatTimeWithoutSeconds(availabilityInfo.sum)})`;
      }

      return "-";
    },
    [availabilityMap],
  );

  const cellRender = useCallback(
    ({ date, text, view }: { date: Date; text: string; view: string }) => {
      if (view !== "month") {
        return <span>{text}</span>;
      }

      if (date < startDate || date > endDate) {
        return (
          <div className="min-h-[100px] h-full flex flex-col justify-center items-center bg-[#e9e9e9] dark:bg-[#292929] rounded font-bold">
            {text}
          </div>
        );
      }

      const day = daysMap.get(format(date, "yyyy-MM-dd'T'00:00:00"));
      if (day) {
        return (
          <div
            className={clsx(
              "min-h-[100px] p-3 flex flex-col justify-center items-center h-full rounded-lg",
              day.dayType === 2
                ? "bg-[#4d7c0f] text-white"
                : day.dayType === 3
                  ? "bg-[#a3e635] text-black"
                  : "bg-[#e9e9e9] text-black dark:bg-[#292929] dark:text-[#eaeaea]",
            )}
          >
            <div className="mb-1 text-base font-bold">{text}</div>
            <div className="text-xs">{localizeFirstLine(day)}</div>
            <div className="text-xs">{day.secondLine}</div>
            <div>{getAvailabilityText(date)}</div>
          </div>
        );
      }

      return (
        <div className="min-h-[100px] h-full flex flex-col justify-center items-center rounded bg-[#e9e9e9] dark:bg-[#292929] font-bold">
          <div>{text}</div>
        </div>
      );
    },
    [daysMap, startDate, endDate, getAvailabilityText, localizeFirstLine],
  );

  const disabledDates = useCallback(
    ({ date, view }: { date: Date; view: string }) => {
      if (view === "month") {
        return (date as Date).getMonth() !== startDate.getMonth();
      }
      return false;
    },
    [startDate],
  );

  const handleOptionChanged = useCallback(
    (event: any) => {
      if (event.fullName === "currentDate") {
        const currentValue = event.value as Date;
        const previousValue = event.previousValue as Date;

        if (currentValue.getMonth() !== previousValue.getMonth()) {
          setCurrentMonth(currentValue);
          setStartDate(
            new Date(
              currentValue.getFullYear(),
              currentValue.getMonth(),
              1,
            ),
          );
          setEndDate(
            new Date(
              currentValue.getFullYear(),
              currentValue.getMonth() + 1,
              0,
            ),
          );

          calendarRef.current?.instance()?.clear();
        }
      }
    },
    [],
  );

  const changeMonth = useCallback((direction: "next" | "previous") => {
    setCurrentMonth((prev) => {
      const next = new Date(
        prev.getFullYear(),
        prev.getMonth() + (direction === "next" ? 1 : -1),
        1,
      );
      setStartDate(new Date(next.getFullYear(), next.getMonth(), 1));
      setEndDate(new Date(next.getFullYear(), next.getMonth() + 1, 0));
      return next;
    });
  }, []);

  const goToToday = useCallback(() => {
    const today = new Date();
    setCurrentMonth(today);
    setStartDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setEndDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  }, []);

  return (
    <ScrollView useNative>
      <div className="p-6">
        {loading ? (
          <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />
        ) : null}
        {showHeader ? (
          <>
            <div className="mb-2 text-2xl font-bold leading-none">
              {t("workPlan.headerEmployee")}
            </div>
            <div className="mb-6 text-base">
              {t("workPlan.headerTextEmployee")}
            </div>
          </>
        ) : null}
        {days.length > 0 && !isMobile ? (
          <Calendar
            ref={calendarRef}
            className="work-plan-for-employee mx-auto max-w-5xl"
            width="100%"
            height={750}
            minZoomLevel="decade"
            firstDayOfWeek={1}
            cellRender={cellRender}
            disabledDates={disabledDates}
            onOptionChanged={handleOptionChanged}
            selectionMode="multiple"
          />
        ) : null}
        {days.length > 0 && isMobile ? (
          <div className="work-plan-mobile">
            <div className="work-plan-mobile-header">
              <Button
                icon="chevronprev"
                stylingMode="text"
                onClick={() => changeMonth("previous")}
              />
              <div className="work-plan-mobile-title">
                {`${_.upperFirst(
                  format(currentMonth, "LLLL", {
                    locale: i18n.language === "en" ? enUS : pl,
                  }),
                )} ${format(currentMonth, "yyyy")}`}
              </div>
              <Button
                icon="chevronnext"
                stylingMode="text"
                onClick={() => changeMonth("next")}
              />
              <Button
                text={i18n.language === "en" ? "Today" : "Dziś"}
                stylingMode="outlined"
                onClick={goToToday}
              />
            </div>
            <div className="work-plan-mobile-legend">
              <div className="work-plan-mobile-legend-item">
                <span className="work-plan-mobile-legend-swatch work-plan-mobile-legend-swatch-holiday" />
                <span>{t("Holiday")}</span>
              </div>
              <div className="work-plan-mobile-legend-item">
                <span className="work-plan-mobile-legend-swatch work-plan-mobile-legend-swatch-free" />
                <span>{t("Day off")}</span>
              </div>
              <div className="work-plan-mobile-legend-item">
                <span className="work-plan-mobile-legend-swatch work-plan-mobile-legend-swatch-remote" />
                <span>{t("remote")}</span>
              </div>
              <div className="work-plan-mobile-legend-item">
                <span>{t("* Reported availability")}</span>
              </div>
            </div>
            <div className="work-plan-mobile-list">
              {eachDayOfInterval({ start: startDate, end: endDate }).map(
                (d) => {
                  const key = format(d, "yyyy-MM-dd'T'00:00:00");
                  const day = daysMap.get(key);
                  const availText = getAvailabilityText(d);
                  const dayShort = _.upperFirst(
                    format(d, "EEEEEE", {
                      locale: i18n.language === "en" ? enUS : pl,
                    }),
                  );
                  const todayStr = new Date().toLocaleDateString("en-CA");
                  const isToday = d.toLocaleDateString("en-CA") === todayStr;
                  return (
                    <div
                      key={key}
                      className={clsx(
                        "work-plan-mobile-day",
                        day?.dayType === 2 && "work-plan-mobile-day-free",
                        day?.dayType === 3 && "work-plan-mobile-day-holiday",
                        !day && "work-plan-mobile-day-empty",
                        isToday && "work-plan-mobile-day-today",
                      )}
                    >
                      <div className="work-plan-mobile-day-left">
                        <div className="work-plan-mobile-day-num">
                          {d.getDate()}
                        </div>
                        <div className="work-plan-mobile-day-name">
                          {dayShort}
                        </div>
                      </div>
                      <div className="work-plan-mobile-day-right">
                        {day?.firstLine ? (
                          <div className="work-plan-mobile-day-line">
                            {localizeFirstLine(day)}
                          </div>
                        ) : null}
                        {day?.secondLine ? (
                          <div className="work-plan-mobile-day-line-sub">
                            {day.secondLine}
                          </div>
                        ) : null}
                        {availText && availText !== "-" ? (
                          <div className="work-plan-mobile-day-availability">
                            {availText}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </div>
        ) : null}

        {!isMobile && (
          <div className="flex flex-wrap justify-center gap-4 pt-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#a3e635]"></div>
              <div className="leading-none">{t("Holiday")}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#4d7c0f]"></div>
              <div className="leading-none">{t("Day off")}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#7e57c2]"></div>
              <div className="leading-none">{t("remote")}</div>
            </div>
            <div className="leading-none">{t("* Reported availability")}</div>
          </div>
        )}
      </div>
    </ScrollView>
  );
}
